import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Users2, Lock, ChevronRight, MapPin, Briefcase } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';

const MyPeersPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  usePageHeader({ title: 'My Peers' }, []);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';

  useEffect(() => {
    if (isOnboarded) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [isOnboarded]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Use /users/team to get all users for peer filtering
      const res = await api.get('/users/team');
      setAllUsers(res.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (userItem) => {
    if (userItem?.first_name && userItem?.last_name) {
      return `${userItem.first_name[0]}${userItem.last_name[0]}`.toUpperCase();
    }
    if (userItem?.full_name) {
      return userItem.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (userItem) => {
    if (userItem?.first_name && userItem?.last_name) {
      return `${userItem.first_name} ${userItem.last_name}`;
    }
    return userItem?.full_name || 'Unknown User';
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  // Filter peers based on current user's role - ONLY ONBOARDED PEERS
  const getPeers = () => {
    const currentRole = user?.role;
    const currentState = user?.state;
    const currentId = user?.id;

    let peers = [];

    // Only show onboarded peers
    const onboardedUsers = allUsers.filter(u => u.is_onboarded);

    if (currentRole === 'admin') {
      // Admin sees other onboarded admins
      peers = onboardedUsers.filter(u => u.role === 'admin' && u.id !== currentId);
    } else if (currentRole === 'supervisor') {
      // Supervisor sees other onboarded supervisors (all states)
      peers = onboardedUsers.filter(u => u.role === 'supervisor' && u.id !== currentId);
    } else if (currentRole === 'lead') {
      // Lead sees other onboarded leads in the SAME STATE only
      peers = onboardedUsers.filter(u => 
        u.role === 'lead' && 
        u.id !== currentId &&
        u.state === currentState
      );
    } else if (currentRole === 'affiliate') {
      // Affiliate sees other onboarded affiliates in SAME STATE only
      peers = onboardedUsers.filter(u => 
        u.role === 'affiliate' && 
        u.id !== currentId &&
        u.state === currentState
      );
    }

    return peers;
  };

  const getPeerDescription = () => {
    const role = user?.role;
    switch (role) {
      case 'admin':
        return 'Other onboarded administrators on the platform';
      case 'supervisor':
        return 'Other onboarded supervisors across all states';
      case 'lead':
        return `Other onboarded leads in ${user?.state || 'your state'}`;
      case 'affiliate':
        return `Other onboarded affiliates in ${user?.state || 'your state'}`;
      default:
        return 'Your peers';
    }
  };

  const renderUserCard = (userItem) => (
    <div
      key={userItem.id}
      className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-[#1a1a1a] hover:bg-slate-100 dark:hover:bg-[#252525] transition-colors cursor-pointer"
      onClick={() => navigate(`/profile/${userItem.id}`)}
      data-testid={`peer-${userItem.id}`}
    >
      <Avatar className="h-12 w-12 border-2 border-white dark:border-[#1a1a1a] shadow-md">
        <AvatarImage src={userItem.profile_picture} />
        <AvatarFallback className={`text-sm font-bold bg-gradient-to-br ${getRoleGradient(userItem.role)} text-white`}>
          {getInitials(userItem)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 dark:text-white truncate">
          {getFullName(userItem)}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          {userItem.state && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {userItem.state}
            </span>
          )}
          {userItem.industry && (
            <span className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              {userItem.industry}
            </span>
          )}
        </div>
      </div>
      <Badge className={`text-xs capitalize px-2.5 py-1 border ${getRoleBadgeStyle(userItem.role)}`}>
        {userItem.role}
      </Badge>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  );

  // Non-onboarded overlay — handled at the route level by
  // <RequireOnboarded> in App.js (iter 96 unified gate). Page-local
  // overlay logic preserved below for back-compat with deep links
  // that bypass App.js but the route guard fires first.
  if (!isOnboarded) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-muted-foreground text-sm">Loading peers...</span>
        </div>
      </div>
    );
  }

  const peers = getPeers();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="my-peers-page">
      <div className="max-w-2xl mx-auto pt-4">
        {/* Header */}
        {/* Content */}
        <Card className="border-0 shadow-xl bg-white/90 dark:bg-[#141414]/90 backdrop-blur-xl">
          <CardContent className="p-6">
            {peers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users2 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  No peers found
                </h3>
                <p className="text-sm text-muted-foreground">
                  {user?.role === 'admin' && 'You are the only administrator on the platform'}
                  {user?.role === 'supervisor' && 'There are no other supervisors yet'}
                  {user?.role === 'lead' && `There are no other leads in ${user?.state || 'your state'}`}
                  {user?.role === 'affiliate' && `There are no other affiliates in your state and industry`}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {peers.map(renderUserCard)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="mt-4 text-center text-sm text-muted-foreground">
          {peers.length} peer{peers.length !== 1 ? 's' : ''} found
        </div>
      </div>
    </div>
  );
};

export default MyPeersPage;
